import { ReactNode, useMemo } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonVariant,
  Hide
} from "shared/components";
import { XClose } from "untitledui-js-base";
import { FormData, Views } from "../types";
import Check from "shared/icons/status-icons/success-icon.svg?react";
import { RefreshCCW01 } from "untitledui-js-base";
import { DecisionAction } from "shared/types";
import { RecommendationMethod } from "types/landlord/verificationTemplate";

const decisionTitle = {
  [Views.MakeDecision]: "Make decision",
  [Views.Conditions]: "Confirm reason(s) for for applying conditions",
  [Views.DecisionSettings]: "Confirm decision settings",
  [Views.Email]: "Confirm message for email"
};

export const Layout = ({
  view,
  onClose,
  onNext,
  onPrev,
  children,
  formData,
  onSubmit,
  showDetailedConditionsView,
  setShowDetailedConditionsView,
  recommendation,
  recommendationMethod,
  isButtonLoading
}: {
  view: Views;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  children: ReactNode;
  formData: FormData;
  onSubmit: () => void;
  showDetailedConditionsView: boolean;
  setShowDetailedConditionsView: React.Dispatch<React.SetStateAction<boolean>>;
  recommendation?: DecisionAction;
  recommendationMethod?: RecommendationMethod;
  isButtonLoading: boolean;
}) => {
  const isContinueDisabled = useMemo(() => {
    if (view === Views.MakeDecision) return !formData.decision;

    return (
      view === Views.Conditions &&
      formData.conditions.length === 0 &&
      !formData.additionalCondition
    );
  }, [
    view,
    formData.decision,
    formData.conditions,
    formData.additionalCondition
  ]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-6 h-20 border-b border-alpha-40 shrink-0">
        <h1 className="text-xl text-content-1000 font-bold flex items-center gap-4">
          {view === Views.Success ? (
            <>
              <Check className="w-6 h-6 text-success-500" />
              Application approved
            </>
          ) : view === Views.Conditions &&
            formData.decision === DecisionAction.Decline ? (
            "Confirm reason(s) for declining"
          ) : (
            decisionTitle[view] || ""
          )}
        </h1>
        {view === Views.Success ? null : (
          <IconButton
            variant={IconButtonVariant.BASE}
            borderLess
            className="!stroke-content-1000 !p-0"
            onClick={onClose}
            Icon={XClose}
          />
        )}
      </div>

      <div className="flex-grow overflow-y-auto">{children}</div>

      <div className="flex justify-between items-center px-6 py-4 border-t border-alpha-100 shrink-0">
        {view === Views.Conditions &&
        !showDetailedConditionsView &&
        formData.decision === recommendation &&
        recommendationMethod === "rule_based" ? (
          <Button
            Icon={RefreshCCW01}
            className="mt-6 mb-4 text-base font-bold leading-6 text-primary-500 flex items-center"
            variant={ButtonVariant.TEXT}
            onClick={() => setShowDetailedConditionsView(true)}
          >
            <span className="ml-2">Use recommend reason(s)</span>
          </Button>
        ) : (
          <div />
        )}

        <div className="flex justify-end gap-2">
          <Hide if={view === Views.Success}>
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.BASE}
              onClick={view === Views.MakeDecision ? onClose : onPrev}
              className="w-[125px]"
            >
              {view !== Views.MakeDecision ? "Back" : "Cancel"}
            </Button>
          </Hide>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={() => {
              if (
                view === Views.Email ||
                (view === Views.DecisionSettings && !formData.sendNotifications)
              )
                onSubmit();
              else if (view === Views.Success) onClose();
              else onNext();
            }}
            className="w-[125px]"
            disabled={isContinueDisabled || isButtonLoading}
            loading={isButtonLoading}
          >
            {view === Views.Success ? "Done" : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
};
